import React, { useState, useEffect } from 'react';
import { Plus, Download, Send, Eye, Edit, Filter, Search } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { invoiceService } from '../../lib/database/invoices';
import { CreateInvoiceModal } from './CreateInvoiceModal';
import { EditInvoiceModal } from './EditInvoiceModal';
import { ViewInvoiceModal } from './ViewInvoiceModal';

export const InvoicesList: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const { data } = await invoiceService.getInvoices();
      if (data) {
        setInvoices(data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleCreateSuccess = () => {
    fetchInvoices(); // Refresh the list
  };

  const handleEditSuccess = () => {
    fetchInvoices(); // Refresh the list
    setSelectedInvoice(null);
  };

  const handleViewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handleEditInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setShowEditModal(true);
  };

  const handleEditFromView = () => {
    setShowViewModal(false);
    setShowEditModal(true);
  };

  const handleSendInvoice = async (invoiceId: string) => {
    try {
      await invoiceService.sendInvoice(invoiceId);
      fetchInvoices(); // Refresh to show updated status
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('Failed to send invoice. Please try again.');
    }
  };

  const handleMarkAsPaid = async (invoiceId: string) => {
    if (window.confirm('Mark this invoice as paid?')) {
      try {
        await invoiceService.markAsPaid(invoiceId);
        fetchInvoices(); // Refresh to show updated status
      } catch (error) {
        console.error('Error marking invoice as paid:', error);
        alert('Failed to update invoice status. Please try again.');
      }
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.client?.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.client?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.total?.toString().includes(searchTerm);
    
    const matchesFilter = filter === 'all' || invoice.status === filter;
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white p-6 rounded-xl h-96"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 mt-2">Manage and track all your invoices</p>
        </div>
        <Button 
          icon={Plus} 
          className="shadow-lg"
          onClick={() => setShowCreateModal(true)}
        >
          Create Invoice
        </Button>
      </div>

      {/* Search and Filters */}
      <Card padding="sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search invoices, clients, amounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80 text-sm"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filter:</span>
            </div>
            <div className="flex space-x-2">
              {['all', 'draft', 'sent', 'paid', 'overdue'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    filter === status
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Enhanced Invoices Table */}
      <Card padding="none" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Invoice</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Client</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Amount</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Status</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Due Date</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-500 bg-gray-50">
                    {searchTerm || filter !== 'all' ? 'No invoices found matching your criteria.' : 'No invoices found. Create your first invoice!'}
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice, index) => (
                  <tr
                    key={invoice.id}
                    className={`transition-all duration-200 hover:bg-blue-50 hover:shadow-sm ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    }`}
                  >
                    <td className="py-4 px-6 border-l-4 border-transparent hover:border-primary-400">
                      <div>
                        <p className="font-semibold text-gray-900">{invoice.invoice_number}</p>
                        <p className="text-sm text-gray-500">
                          Created {formatDate(invoice.created_at)}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-gray-900">{invoice.client?.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{invoice.client?.company || 'No company'}</p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <p className="font-bold text-lg text-gray-900">
                        {formatCurrency(Number(invoice.total))}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <StatusBadge status={invoice.status} />
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-gray-900 font-medium">{formatDate(invoice.due_date)}</p>
                      {invoice.status === 'overdue' && (
                        <p className="text-sm text-error-600 font-medium">
                          {Math.ceil((new Date().getTime() - new Date(invoice.due_date).getTime()) / (1000 * 60 * 60 * 24))} days overdue
                        </p>
                      )}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Eye}
                          onClick={() => handleViewInvoice(invoice)}
                          className="hover:bg-blue-100"
                        >
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Edit}
                          onClick={() => handleEditInvoice(invoice)}
                          className="hover:bg-yellow-100"
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Download}
                          className="hover:bg-green-100"
                        >
                          PDF
                        </Button>
                        {invoice.status === 'draft' && (
                          <Button 
                            variant="primary" 
                            size="sm" 
                            icon={Send}
                            onClick={() => handleSendInvoice(invoice.id)}
                          >
                            Send
                          </Button>
                        )}
                        {invoice.status === 'sent' && (
                          <Button 
                            variant="success" 
                            size="sm"
                            onClick={() => handleMarkAsPaid(invoice.id)}
                          >
                            Mark Paid
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card padding="sm" className="text-center bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <p className="text-2xl font-bold text-blue-700">{filteredInvoices.length}</p>
          <p className="text-sm text-blue-600">{searchTerm || filter !== 'all' ? 'Filtered' : 'Total'} Invoices</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-success-50 to-success-100 border-success-200">
          <p className="text-2xl font-bold text-success-700">
            {filteredInvoices.filter(i => i.status === 'paid').length}
          </p>
          <p className="text-sm text-success-600">Paid</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-warning-50 to-warning-100 border-warning-200">
          <p className="text-2xl font-bold text-warning-700">
            {filteredInvoices.filter(i => i.status === 'sent').length}
          </p>
          <p className="text-sm text-warning-600">Pending</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-error-50 to-error-100 border-error-200">
          <p className="text-2xl font-bold text-error-700">
            {filteredInvoices.filter(i => i.status === 'overdue').length}
          </p>
          <p className="text-sm text-error-600">Overdue</p>
        </Card>
      </div>

      {/* Modals */}
      <CreateInvoiceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditInvoiceModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedInvoice(null);
        }}
        onSuccess={handleEditSuccess}
        invoice={selectedInvoice}
      />

      <ViewInvoiceModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedInvoice(null);
        }}
        onEdit={handleEditFromView}
        invoice={selectedInvoice}
      />
    </div>
  );
};